import React, { useEffect, useState } from 'react';
import { Alert, Button, Card } from '@/components/ui';
import { VendorInvoice, VendorInvoiceCreateData, VendorInvoiceUpdateData } from '../../../types/VendorInvoice';
import VendorInvoiceList from './VendorInvoiceList';
import VendorInvoiceModal from './VendorInvoiceModal';
import { projectService } from '../../../services/projectService';

interface VendorInvoicesTabProps {
  projectId: number;
}

const VendorInvoicesTab: React.FC<VendorInvoicesTabProps> = ({ projectId }) => {
  const [vendorInvoices, setVendorInvoices] = useState<VendorInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<VendorInvoice | undefined>();
  const [refreshing, setRefreshing] = useState(false);
  // Upload problems are reported inline above the list (STATE-06), not in a browser alert.
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    loadVendorInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadVendorInvoices = async () => {
    try {
      setLoading(true);
      const invoiceData = await (projectService as any).getProjectVendorInvoices(projectId);
      setVendorInvoices(invoiceData || []);
    } catch (error) {
      console.error('Error loading vendor invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddInvoice = () => {
    setEditingInvoice(undefined);
    setIsModalVisible(true);
  };

  const handleEditInvoice = (invoice: VendorInvoice) => {
    setEditingInvoice(invoice);
    setIsModalVisible(true);
  };

  const handleRetryUpload = async (invoiceId: number, attachmentId: number) => {
    // Mirrors the MoTT invoice behaviour: the original file isn't kept, so it can't be re-sent from here.
    setUploadNotice("This attachment didn't upload. To retry, delete the failed attachment and upload the file again – the file name is cleaned automatically.");
  };

  const handleSaveInvoice = async (invoiceData: VendorInvoiceCreateData | VendorInvoiceUpdateData, file?: File) => {
    try {
      setRefreshing(true);

      if (editingInvoice) {
        await (projectService as any).updateVendorInvoice(projectId, editingInvoice.id, invoiceData);
      } else {
        const savedInvoice = await (projectService as any).createVendorInvoice(projectId, invoiceData);
        if (file && savedInvoice?.id) {
          try {
            await (projectService as any).uploadVendorInvoiceAttachment(projectId, savedInvoice.id, file);
          } catch (fileError) {
            console.error('Error uploading file:', fileError);
            setUploadNotice("Vendor invoice created, but the file didn't upload. Upload it again from the invoice.");
          }
        }
      }

      await loadVendorInvoices();
    } catch (error) {
      console.error('Error saving vendor invoice:', error);
      throw error;
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId: number) => {
    try {
      await (projectService as any).deleteVendorInvoice(projectId, invoiceId);
      await loadVendorInvoices();
    } catch (error) {
      console.error('Error deleting vendor invoice:', error);
      throw error;
    }
  };

  const handleViewAttachment = (attachment: any, invoice: VendorInvoice) => {
    if (attachment?.attachmentURL?.startsWith('placeholder://')) {
      setUploadNotice(`"${attachment.attachmentName}" didn't upload, so it can't be opened. Upload the file again.`);
      return;
    }
    window.open(
      `/api/projects/${projectId}/vendor-invoices/${invoice.id}/attachments/${attachment.id}/download`,
      '_blank'
    );
  };

  const getTotalInvoiceValue = () => {
    return vendorInvoices.reduce((total, inv) => total + (inv.invoiceValue || 0), 0);
  };

  return (
    <Card>
      <Card.Header className="tw-flex tw-justify-between tw-items-center">
        <h6 className="tw-mb-0">Vendor Invoices</h6>
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-text-content-muted">
            Total:{' '}
            <span className="tw-font-bold">
              ${getTotalInvoiceValue().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <Button variant="primary" size="sm" onClick={handleAddInvoice} disabled={refreshing}>
            <i className="bi bi-plus-lg tw-mr-2"></i>
            Add Invoice
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <VendorInvoiceList
          invoices={vendorInvoices}
          onEdit={handleEditInvoice}
          onDelete={handleDeleteInvoice}
          onViewAttachment={handleViewAttachment}
          onRetryUpload={handleRetryUpload}
          loading={loading}
        />
      </Card.Body>

      <VendorInvoiceModal
        visible={isModalVisible}
        onClose={() => {
          setIsModalVisible(false);
          setEditingInvoice(undefined);
        }}
        onSave={handleSaveInvoice}
        invoice={editingInvoice}
        projectId={projectId}
      />
    </Card>
  );
};

export default VendorInvoicesTab;

